import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useDatasetModeRequest } from "@/features/data-curation/hooks/useDatasetMode";
import { useDailyPrompts } from "@/features/usage/hooks/useLimit";
import { reportLimitError } from "@/features/usage/limitErrors";
import { useStreams } from "../streams/StreamsProvider";
import { openStream } from "../streams/openStream";
import {
  STREAM_FADE_MS,
  type RenderChunk,
  type StreamingState,
} from "../streams/streamStore";
import type {
  Message,
  StreamChunk,
} from "../types";
import { AGENT_CONFIG_PATH, queryKeys } from "../types";

let streamCounter = 0;

export interface ChatStreamOptions {
  /** Agent config the backend runs. Defaults to the Paperclip chat agent. */
  agentConfigPath?: string;
  /** Route prefix to navigate to once a session is created (e.g. "/chat"). */
  routeBase?: string;
  /** Called with a new session's id instead of navigating to `routeBase`. */
  onSessionCreated?: (sessionId: string) => void;
  /** Data curation a new session belongs to (sent with its first message). */
  extractionId?: string;
  /** Marks every message as coming from the Data Curation page chat. */
  dataCurationChat?: boolean;
  /** The fast dataset beside the chat, sent with every message. */
  fastDatasetId?: string;
  /** Or the share token of a shared fast dataset, which the agent gets read-only. */
  fastDatasetShare?: string;
}

export const useChatStream = (
  sessionId: string | undefined,
  options: ChatStreamOptions = {},
) => {
  const agentConfigPath = options.agentConfigPath ?? AGENT_CONFIG_PATH;
  const routeBase = options.routeBase ?? "/chat";
  const onSessionCreatedRef = useRef(options.onSessionCreated);
  onSessionCreatedRef.current = options.onSessionCreated;
  const extractionIdRef = useRef(options.extractionId);
  extractionIdRef.current = options.extractionId;
  const dataCurationChatRef = useRef(options.dataCurationChat);
  dataCurationChatRef.current = options.dataCurationChat;
  const fastDatasetIdRef = useRef(options.fastDatasetId);
  fastDatasetIdRef.current = options.fastDatasetId;
  const fastDatasetShareRef = useRef(options.fastDatasetShare);
  fastDatasetShareRef.current = options.fastDatasetShare;
  const datasetMode = useDatasetModeRequest();
  const datasetModeRef = useRef(datasetMode);
  datasetModeRef.current = datasetMode;
  const { createStream, updateStream, abortStreamsForSession, removeStream, getStreamForSession, getStream } =
    useStreams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const { refetchUsage, isAtLimit: promptsAtLimit } = useDailyPrompts();
  const promptsAtLimitRef = useRef(promptsAtLimit);
  promptsAtLimitRef.current = promptsAtLimit;
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const activeStreamIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeStreamIdRef.current = null;
  }, [sessionId]);

  const currentStreamingState =
    getStreamForSession(sessionId) ??
    (activeStreamIdRef.current ? getStream(activeStreamIdRef.current) : undefined);

  const sendMessage = useCallback(
    async (message: string, opts?: { sessionTitle?: string }) => {
      const sid = sessionIdRef.current;

      if (promptsAtLimitRef.current) {
        toast.error("Daily query limit reached.");
        return;
      }

      if (sid) {
        abortStreamsForSession(sid);
      }

      const streamId = `stream-${++streamCounter}`;
      const abortController = new AbortController();

      const userMessage: Message = {
        id: `pending-${streamId}`,
        role: "user",
        content: message,
        created_at: new Date().toISOString(),
        tool_calls: [],
        tool_call_id: null,
      };

      const initialState: StreamingState = {
        streamId,
        sessionId: sid,
        isStreaming: true,
        fadeHold: false,
        streamingMessage: null,
        streamingUserMessage: userMessage,
        streamingToolCalls: [],
        toolExecutionPhase: "none",
        toolProgress: null,
        toolResults: new Map(),
        renderChunks: [],
        abortController,
      };

      createStream(initialState);
      activeStreamIdRef.current = streamId;

      const body: Record<string, unknown> = {
        message,
        agent_config_path: agentConfigPath,
        executeTools: true,
      };
      if (sid) body.sessionId = sid;
      // Explicit title only matters when creating a new session; the backend
      // uses it verbatim and skips its auto/LLM titling.
      if (!sid && opts?.sessionTitle?.trim()) {
        body.session_title = opts.sessionTitle.trim();
      }
      if (!sid && extractionIdRef.current) {
        body.extraction_id = extractionIdRef.current;
      }
      if (fastDatasetIdRef.current) body.fast_dataset_id = fastDatasetIdRef.current;
      else if (fastDatasetShareRef.current) body.fast_dataset_share = fastDatasetShareRef.current;
      if (dataCurationChatRef.current) {
        body.data_curation_chat = true;
        if (datasetModeRef.current) body.dataset_mode = datasetModeRef.current;
        body.user_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      const update = (
        updater: (prev: StreamingState) => StreamingState,
      ) => updateStream(streamId, updater);

      // The component that started a new session may be gone by the time the
      // stream ends (e.g. the Data Curation composer hands off to its chat).
      let streamSessionId = sid;
      let lastContentAt = 0;
      let fadeReleaseTimer: ReturnType<typeof setTimeout> | undefined;
      let ended = false;

      const scheduleFadeRelease = (opts: { remove: boolean }) => {
        if (fadeReleaseTimer !== undefined) clearTimeout(fadeReleaseTimer);
        const wait = Math.max(0, STREAM_FADE_MS - (Date.now() - lastContentAt));
        fadeReleaseTimer = setTimeout(() => {
          if (opts.remove) {
            const resolvedSessionId = streamSessionId ?? sessionIdRef.current;
            if (resolvedSessionId) {
              queryClient
                .invalidateQueries({
                  queryKey: queryKeys.messages(resolvedSessionId),
                })
                .then(() => {
                  removeStream(streamId);
                });
            } else {
              removeStream(streamId);
            }
            return;
          }
          update((s) => ({ ...s, fadeHold: false }));
        }, wait);
      };

      const handleMessage = (raw: string) => {
        let chunk: StreamChunk;
        try {
          chunk = JSON.parse(raw) as StreamChunk;
        } catch {
          return;
        }

        switch (chunk.type) {
          case "session_id": {
            const newSessionId = chunk.session_id;
            streamSessionId = newSessionId;
            update((s) => ({ ...s, sessionId: newSessionId }));
            queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
            if (onSessionCreatedRef.current) {
              onSessionCreatedRef.current(newSessionId);
            } else {
              // Preserve any query string (e.g. Peer Review's ?doc=<id>) so the
              // paper reference survives the in-place navigate. Chat has none.
              navigate(`${routeBase}/${newSessionId}${window.location.search}`, {
                replace: true,
              });
            }
            // The 3-5 word LLM title is written ~1s after the session is
            // created, in parallel with generation. The refetch above races
            // ahead of it (it fires at session creation), and we can't rely on
            // the terminal "done" event landing after the title (some engines
            // don't surface it to the client). Re-fetch the sessions list a
            // couple of times so the sidebar swaps the truncated title for the
            // LLM one without a manual refresh.
            [1500, 4000].forEach((delay) =>
              window.setTimeout(
                () =>
                  queryClient.invalidateQueries({
                    queryKey: ["chat-sessions"],
                  }),
                delay,
              ),
            );
            break;
          }

          case "content": {
            lastContentAt = Date.now();
            update((s) => {
              const assistantMsg: Message = s.streamingMessage ?? {
                id: `assistant-${streamId}`,
                role: "assistant",
                content: "",
                created_at: new Date().toISOString(),
                tool_calls: [],
                tool_call_id: null,
                isStreaming: true,
              };
              const newContent = (assistantMsg.content ?? "") + chunk.data;

              const chunks = [...s.renderChunks];
              const last = chunks[chunks.length - 1];
              if (last && last.type === "content") {
                chunks[chunks.length - 1] = {
                  type: "content",
                  text: last.text + chunk.data,
                };
              } else {
                chunks.push({ type: "content", text: chunk.data });
              }

              return {
                ...s,
                streamingMessage: { ...assistantMsg, content: newContent },
                renderChunks: chunks,
                fadeHold: true,
                lastContentAt,
              };
            });
            break;
          }

          case "tool_calls": {
            update((s) => {
              const calls = chunk.data;
              const chunks: RenderChunk[] = [
                ...s.renderChunks,
                { type: "tool_calls", calls, phase: "pending" },
              ];
              return {
                ...s,
                streamingToolCalls: [...s.streamingToolCalls, ...calls],
                renderChunks: chunks,
              };
            });
            break;
          }

          case "tool_calls_delta": {
            update((s) => {
              const toolCalls = [...s.streamingToolCalls];
              for (const delta of chunk.data) {
                if (!toolCalls[delta.index]) {
                  toolCalls[delta.index] = {
                    id: delta.id ?? "",
                    function: { name: "", arguments: "" },
                  };
                }
                const tc = { ...toolCalls[delta.index] };
                if (delta.id) tc.id = delta.id;
                if (delta.function) {
                  const fn = { ...tc.function };
                  if (delta.function.name) fn.name = delta.function.name;
                  if (delta.function.arguments)
                    fn.arguments += delta.function.arguments;
                  tc.function = fn;
                }
                toolCalls[delta.index] = tc;
              }

              const chunks = [...s.renderChunks];
              const lastChunk = chunks[chunks.length - 1];
              if (lastChunk && lastChunk.type === "tool_calls") {
                chunks[chunks.length - 1] = {
                  ...lastChunk,
                  calls: [...toolCalls],
                };
              } else {
                chunks.push({
                  type: "tool_calls",
                  calls: [...toolCalls],
                  phase: "pending",
                });
              }

              return { ...s, streamingToolCalls: toolCalls, renderChunks: chunks };
            });
            break;
          }

          case "tool_execution_started": {
            update((s) => {
              const chunks = [...s.renderChunks];
              for (let i = chunks.length - 1; i >= 0; i--) {
                if (chunks[i].type === "tool_calls") {
                  chunks[i] = {
                    ...(chunks[i] as RenderChunk & { type: "tool_calls" }),
                    phase: "executing",
                  };
                  break;
                }
              }
              return {
                ...s,
                toolExecutionPhase: "executing",
                streamingToolCalls: [],
                renderChunks: chunks,
              };
            });
            break;
          }

          case "tool_execution_result": {
            update((s) => {
              const results = new Map(s.toolResults);
              for (const r of chunk.results) {
                results.set(r.tool_call_id, r);
              }
              const chunks = [...s.renderChunks];
              const resultIds = new Set(chunk.results.map((r: { tool_call_id: string }) => r.tool_call_id));
              for (let i = chunks.length - 1; i >= 0; i--) {
                if (chunks[i].type === "tool_calls") {
                  const tcChunk = chunks[i] as RenderChunk & { type: "tool_calls" };
                  const allHaveResults = tcChunk.calls.every(
                    (c) => results.has(c.id) || resultIds.has(c.id),
                  );
                  if (allHaveResults) {
                    chunks[i] = { ...tcChunk, phase: "complete" };
                  }
                }
              }
              return {
                ...s,
                toolResults: results,
                toolExecutionPhase: "complete",
                toolProgress: null,
                renderChunks: chunks,
              };
            });
            break;
          }

          case "tool_progress": {
            update((s) => ({
              ...s,
              toolProgress: {
                command: chunk.command,
                completed: chunk.completed,
                failed: chunk.failed,
                total: chunk.total,
                elapsed_s: chunk.elapsed_s,
              },
            }));
            break;
          }

          case "user_message_saved": {
            update((s) => ({ ...s, userMessageId: chunk.message_id }));
            break;
          }

          case "done": {
            ended = true;
            update((s) => ({ ...s, isStreaming: false, fadeHold: true }));
            activeStreamIdRef.current = null;
            scheduleFadeRelease({ remove: true });
            queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
            break;
          }

          case "error": {
            ended = true;
            toast.error(chunk.error, {
              id: "chat-stream-error",
              testId: "chat-stream-error",
            });
            activeStreamIdRef.current = null;
            update((s) => ({
              ...s,
              error: chunk.error,
              isStreaming: false,
              fadeHold: true,
            }));
            scheduleFadeRelease({ remove: false });
            break;
          }

          default:
            break;
        }
      };

      try {
        await openStream({
          path: "/api/chat/stream",
          body,
          abortController,
          // The agent's CLI keeps this token for the whole turn, which can
          // run for an hour, so start each turn with a token of a full hour.
          getToken: () => getIdToken(true),
          onAccepted: refetchUsage,
          onMessage: handleMessage,
          onError: (err) => {
            if (abortController.signal.aborted) return;
            ended = true;
            const limitMessage = reportLimitError(err, "chat", {
              refetchUsage,
              quotaAlertVisible: promptsAtLimitRef.current,
            });
            const msg =
              limitMessage ??
              (err instanceof Error ? err.message : "Stream connection failed");
            if (!limitMessage) {
              toast.error(msg, {
                id: "chat-stream-error",
                testId: "chat-stream-error",
              });
            }
            update((s) => ({
              ...s,
              error: msg,
              isStreaming: false,
              fadeHold: true,
            }));
            scheduleFadeRelease({ remove: false });
          },
          // A connection can close without "done", for example when a proxy
          // drops it. The agent may still run on the server; the session
          // status shows that, so the tab must not keep its own stream open.
          onClose: () => {
            if (ended || abortController.signal.aborted) return;
            ended = true;
            update((s) => ({ ...s, isStreaming: false, fadeHold: true }));
            activeStreamIdRef.current = null;
            scheduleFadeRelease({ remove: true });
            queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
            const closedSessionId = streamSessionId ?? sessionIdRef.current;
            if (closedSessionId) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.sessionStatus(closedSessionId),
              });
            }
          },
        });
      } catch {
        if (abortController.signal.aborted) {
          update((s) => ({
            ...s,
            isStreaming: false,
            aborted: true,
            fadeHold: true,
          }));
          scheduleFadeRelease({ remove: false });
        }
      }
    },
    [
      createStream,
      updateStream,
      abortStreamsForSession,
      removeStream,
      queryClient,
      navigate,
      agentConfigPath,
      routeBase,
      getIdToken,
      refetchUsage,
    ],
  );

  return { currentStreamingState, sendMessage };
};
